import React from 'react';
import { Package, MapPin, User, CheckCircle2, History, Lock } from 'lucide-react';

function DeliveryItem({
  delivery,
  onClick,
  isLast,
  disabled = false,
  isLocked = false,
  onShowHistory
}) {
  const getStatusColor = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'pending':
        return 'bg-gray-100 text-gray-700';
      case 'encamino':
        return 'bg-blue-100 text-blue-700';
      case 'entregado':
        return 'bg-brand-green/20 text-brand-green';
      case 'cancelado':
        return 'bg-red-100 text-red-700';
      case 'rechazado':
        return 'bg-orange-100 text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusText = (status) => {
    const statusLower = (status || '').toLowerCase();
    switch (statusLower) {
      case 'pending':
        return 'Pendiente';
      case 'encamino':
        return 'En camino';
      case 'entregado':
        return 'Entregado';
      case 'cancelado':
        return 'Cancelado';
      case 'rechazado':
        return 'Rechazado';
      default:
        return status;
    }
  };

  /**
   * Get package status icon color
   * Green: with_driver
   * Blue: in_fastmax
   * Red: returned, lost_or_damaged
   * Gray: in_warehouse or other
   */
  const getPackageStatusColor = (packageStatus) => {
    switch (packageStatus) {
      case 'with_driver':
        return 'text-brand-green';
      case 'in_fastmax':
        return 'text-blue-500';
      case 'returned':
      case 'lost_or_damaged':
        return 'text-red-500';
      case 'out_for_delivery':
        return 'text-purple-500';
      case 'delivered':
        return 'text-brand-green';
      case 'postponed_with_driver':
        return 'text-orange-500';
      default:
        return 'text-gray-400'; // in_warehouse or unknown
    }
  };

  const handleHistoryClick = (e) => {
    e.stopPropagation(); // Prevent triggering onClick
    onShowHistory(delivery);
  };

  return (
    <div className={!isLast ? 'border-b border-gray-100' : ''}>
      <div className="flex items-start gap-3 px-5 py-4">
        {/* Main clickable area */}
        <button
          onClick={onClick}
          disabled={disabled}
          className={`
            flex-1 flex items-start gap-3 text-left transition-colors
            ${disabled
              ? 'cursor-not-allowed'
              : isLocked
                ? 'cursor-pointer opacity-70'
                : 'cursor-pointer'
            }
          `}
        >
          {/* Sequence Badge */}
          <div className={`
            flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm
            ${disabled || isLocked ? 'bg-gray-200 text-gray-500' : 'bg-brand-primary text-white'}
          `}>
            {isLocked ? <Lock className="w-4 h-4" /> : delivery.sequence}
          </div>

          {/* Delivery Info */}
          <div className="flex-1 min-w-0">
            {/* Recipient Name */}
            <div className="flex items-center gap-2 mb-1">
              <User className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="font-semibold text-gray-900 truncate">
                {delivery.recipient_name}
              </span>
            </div>

            {/* Product */}
            <div className="flex items-start gap-2 mb-1">
              <Package className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-gray-600 line-clamp-2">
                {delivery.product}
              </span>
            </div>

            {/* Provider */}
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <span className="text-xs text-gray-500 truncate">
                Proveedor: {delivery.user_name}
              </span>
            </div>

            {/* Status Badge */}
            <div className="flex items-center gap-2">
              <span className={`
                inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
                ${getStatusColor(delivery.status)}
              `}>
                {delivery.is_submitted && (
                  <CheckCircle2 className="w-3.5 h-3.5" />
                )}
                {getStatusText(delivery.status)}
              </span>
              
              {/* ID Badge */}
              <span className="text-xs text-gray-400">
                ID: {delivery.id}
              </span>

              {isLocked && !disabled && (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700">
                  <Lock className="w-3 h-3" />
                  Sigue la secuencia
                </span>
              )}
            </div>
          </div>
        </button>

        {/* Action Buttons (Non-clickable area) */}
        <div className="flex flex-col gap-2 flex-shrink-0">
          {/* Package Status Indicator */}
          <button
            // onClick={handleHistoryClick}
            disabled={disabled}
            className={`
              p-2 rounded-lg transition-colors
              ${disabled
                ? 'bg-gray-100 cursor-not-allowed'
                : 'bg-gray-50 hover:bg-gray-100'
              }
            `}
            title={`Estado: ${delivery.package_status || 'Sin paquete'}`}
          >
            <Package className={`w-5 h-5 ${getPackageStatusColor(delivery.package_status)}`} />
          </button>

          {/* History Button */}
          <button
            onClick={handleHistoryClick}
            disabled={disabled}
            className={`
              p-2 rounded-lg transition-colors
              ${disabled
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-brand-primary'
              }
            `}
            title="Ver historial del paquete"
          >
            <History className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeliveryItem;
